import Card from "@material-ui/core/Card";
import Collapse from "@material-ui/core/Collapse";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { useEffect, useRef, useState } from "react";
import { splitByBoolean } from "../../../../../../../../../../common/util/Array/orderByBoolean";
import { isAndroid } from "../../../../../../../../../../common/util/osCheck";
import { User, UserForRanking } from "../../../../../../../../User/types";
import {
    ChangeCommenterAvatarElement,
    OriginKey,
    Post,
    PostComment,
} from "../../../../../types";
import { InputFieldArea } from "../InputFieldArea";
import { MutedComment } from "../MutedComment";
import { CommentLeftAvatarArea } from "./CommentLeftAvatarArea";
import { CommentRightContents } from "./CommentRightContents";
import { checkUnreadComment, getSendText } from "./functions";

export const collapseTimeout = 500;
export const collapseTimeStep = 10;

export function CommentCard({
    post,
    comment,
    user,
    tmpMutedUserIds,
    setTmpMutedUserIds,
    comments,
    setComments,
    setCommentedUsers,
    deleteComment,
    commentedUsers,
    changeCommenterAvatarElement,
    refreshUsersConnectionHeightList,
    unreadCommentNos,
    depth = 0,
    showOnlyUnreadComment,
    addTmpComment,
    addCommentCount,
    isLastCard,
    setShowOnlyUnreadComment,
    keepRefreshing,
    originKey,
}: {
    post: Post;
    comment: PostComment;
    user?: User;
    tmpMutedUserIds: number[];
    setTmpMutedUserIds: (ids: number[]) => void;
    comments: PostComment[];
    setComments: (comments: PostComment[]) => void;
    setCommentedUsers: (users: UserForRanking[]) => void;
    deleteComment: (
        targetComment: PostComment,
        deleteFromServerSie: () => Promise<void>
    ) => Promise<void>;
    commentedUsers: UserForRanking[];
    changeCommenterAvatarElement: ChangeCommenterAvatarElement;
    refreshUsersConnectionHeightList: () => void;
    unreadCommentNos: number[];
    depth?: number;
    showOnlyUnreadComment: boolean;
    addTmpComment: (tmpComment: PostComment) => void;
    addCommentCount: (addedCount: number) => void;
    isLastCard?: boolean;
    setShowOnlyUnreadComment: (onlyUnread: boolean) => void;
    keepRefreshing: () => Promise<void>;
    originKey: OriginKey;
}) {
    const isUnread = unreadCommentNos.includes(comment.commentNo);

    const refreshContainer = useRef(() => {});
    refreshContainer.current = refreshUsersConnectionHeightList;
    useEffect(() => {
        if (typeof refreshContainer.current === "function") {
            const imgElements = Array.from(
                document.getElementsByClassName(
                    `social-post-image-${comment.postedUserId}-${comment.postNo}-${comment.commentNo}`
                )
            );
            if (imgElements.length < 1) {
                return;
            }
            refreshContainer.current();
            imgElements.forEach(imgElement => {
                imgElement.addEventListener("load", refreshContainer.current);
            });
        }
    }, [comment]);

    const commentsAndLikesToThisComment = comments.filter(
        com => com.replyTargetCommentNo === comment.commentNo
    );

    const [shouldShowReply, setShouldShowReply] = useState(
        commentsAndLikesToThisComment.some(com =>
            checkUnreadComment(com, unreadCommentNos, comments)
        )
    );

    const inputRef = useRef<HTMLInputElement>(null);
    const [isInputAreaHidden, setInputAreaHidden] = useState(
        unreadCommentNos.length > 0
    );

    const [
        unreadCommentsAndLikesToThisComment,
        readCommentsAndLikesToThisComment,
    ] = splitByBoolean(commentsAndLikesToThisComment, com =>
        checkUnreadComment(com, unreadCommentNos, comments)
    );
    const commentsAndLikesToShow = showOnlyUnreadComment
        ? unreadCommentsAndLikesToThisComment
        : unreadCommentsAndLikesToThisComment.concat(
              readCommentsAndLikesToThisComment
          );

    const c = useStyles({
        hasBorder: comment.commentNo < 1 || isUnread,
        depth,
        shouldShowReply,
        isLastCard,
    });

    const commentedUser = commentedUsers.find(
        u => u.userId === comment.commentedUserId
    );
    const replyTargetUser =
        (comment.replyTargetUserId &&
            commentedUsers.find(u => u.userId === comment.replyTargetUserId)) ||
        undefined;
    if (!commentedUser) {
        return null;
    }
    if (user && tmpMutedUserIds.includes(commentedUser.userId)) {
        return (
            <MutedComment
                key={`${comment.commentedUserId}-${comment.commentNo}`}
                userId={user.userId}
                comment={comment}
                tmpMutedUserIds={tmpMutedUserIds}
                setTmpMutedUserIds={setTmpMutedUserIds}
            />
        );
    }

    return (
        <>
            <Card
                id={getCommentCardId(
                    originKey,
                    comment.postedUserId,
                    comment.postNo,
                    comment.commentNo
                )}
                className={c.card}
                elevation={3}
                onClick={() => {
                    if (comment.isLike) {
                        return;
                    }
                    if (isInputAreaHidden) {
                        setInputAreaHidden(false);
                        setShouldShowReply(true);
                        return;
                    }
                    if (shouldShowReply) {
                        // When it's opened, just close it
                        setShouldShowReply(false);
                        return;
                    }
                    setShouldShowReply(true);
                }}
            >
                <CommentLeftAvatarArea
                    postedUser={commentedUser}
                    changeCommenterAvatarElement={changeCommenterAvatarElement}
                    commentNo={comment.commentNo}
                />

                <CommentRightContents
                    post={post}
                    comment={comment}
                    user={user}
                    tmpMutedUserIds={tmpMutedUserIds}
                    setTmpMutedUserIds={setTmpMutedUserIds}
                    comments={comments}
                    setComments={setComments}
                    setCommentedUsers={setCommentedUsers}
                    deleteComment={deleteComment}
                    commentedUser={commentedUser}
                    commentsAndLikesThisComment={commentsAndLikesToThisComment}
                    showReply={() => {
                        setShouldShowReply(true);
                        setShowOnlyUnreadComment(false);
                    }}
                    replyTargetUser={replyTargetUser}
                />
            </Card>

            <Collapse
                in={shouldShowReply}
                onExiting={keepRefreshing}
                onEntering={keepRefreshing}
                timeout={collapseTimeout}
                onEntered={() => {
                    if (isAndroid) {
                        return; // focusをするとアンドロイドではソフトウェアキーボードが出てしまう問題がある
                    }
                    inputRef.current?.focus();
                }}
                className={c.collapse}
                classes={{ root: c.collapseRoot }}
            >
                <div className={c.collapseInner}>
                    {!comment.isLike && !isInputAreaHidden && (
                        <InputFieldArea
                            autoFocus
                            inputRef={inputRef}
                            user={user}
                            sendText={getSendText(
                                addTmpComment,
                                post,
                                addCommentCount,
                                comment
                            )}
                            className={c.inputField}
                            placeholder={"Write your reply..."}
                            onEdit={refreshUsersConnectionHeightList}
                        />
                    )}

                    {commentsAndLikesToShow.map((com, i) => (
                        <CommentCard
                            key={com.commentNo}
                            post={post}
                            comment={com}
                            user={user}
                            tmpMutedUserIds={tmpMutedUserIds}
                            setTmpMutedUserIds={setTmpMutedUserIds}
                            comments={comments}
                            setComments={setComments}
                            setCommentedUsers={setCommentedUsers}
                            deleteComment={deleteComment}
                            commentedUsers={commentedUsers}
                            changeCommenterAvatarElement={
                                refreshUsersConnectionHeightList
                            }
                            refreshUsersConnectionHeightList={
                                refreshUsersConnectionHeightList
                            }
                            unreadCommentNos={unreadCommentNos}
                            depth={depth + 1}
                            showOnlyUnreadComment={showOnlyUnreadComment}
                            addTmpComment={addTmpComment}
                            addCommentCount={addCommentCount}
                            isLastCard={i === commentsAndLikesToShow.length - 1}
                            setShowOnlyUnreadComment={setShowOnlyUnreadComment}
                            keepRefreshing={keepRefreshing}
                            originKey={originKey}
                        />
                    ))}
                </div>
            </Collapse>
        </>
    );
}
const useStyles = makeStyles<
    Theme,
    {
        hasBorder: boolean;
        depth: number;
        shouldShowReply: boolean;
        isLastCard?: boolean;
    }
>(theme => ({
    card: ({ hasBorder }) => ({
        margin: "5px 0 5px 5px",
        display: "flex",
        padding: "5px 7px 5px 5px",
        border: hasBorder
            ? `solid 2px ${theme.palette.primary.light}`
            : undefined,
        transition: "all 500ms",
    }),
    inputField: {
        margin: "10px 0px 15px 10px",
    },
    collapse: ({ depth, shouldShowReply, isLastCard }) => ({
        backgroundColor: "rgba(0,0,0,0.1)",
        paddingLeft: depth < 5 ? 10 : undefined,
        marginLeft: 5,
        marginTop: shouldShowReply ? -5 : 0,
        marginBottom: shouldShowReply ? (isLastCard ? 5 : 30) : 0,
        paddingTop: shouldShowReply ? 3 : 0,
        transition: `all ${collapseTimeout}ms`,
        borderBottomLeftRadius: 8,
    }),
    collapseRoot: ({ depth }) => ({
        paddingBottom: depth < 5 ? 2 : undefined,
    }),
    collapseInner: {
        backgroundColor: "white",
        paddingTop: 5,
        paddingBottom: 1,
        transition: `all ${collapseTimeout}ms`,
    },
}));

export function getCommentCardId(
    originKey: OriginKey,
    postedUserId: number,
    postNo: number,
    commentNo: number
) {
    return `${originKey}-SocialCommentCard-${postedUserId}-${postNo}-${commentNo}`;
}
